import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getActorDisplayName } from '../../../utils/roleUtils';
import { Checkbox } from '../../ui/checkbox';
import { Button } from '../../ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Trash2 } from 'lucide-react';
import type { RoleOption } from '../../../services/api/onboardingService';
import { formatShortDate } from '../../../utils/dateUtils';
import type { TableMode, UserRow } from './userManagementTypes';

interface UserInvitationsTableProps {
    users: UserRow[];
    roles: RoleOption[];
    tableMode?: TableMode;
    onRoleChange?: (userId: string, roleId: string) => void;
    onRemove?: (userId: string) => Promise<boolean | void> | boolean | void;
    onSendReminder?: (user: UserRow) => Promise<void> | void;
    onBulkRoleAssign?: (userIds: string[], roleId: string) => void;
    emptyMessage?: string;
}

export default function UserInvitationsTable({
    users,
    roles,
    tableMode = 'management',
    onRoleChange,
    onRemove,
    onSendReminder,
    onBulkRoleAssign,
    emptyMessage = 'No invitations yet.',
}: UserInvitationsTableProps) {
    const { t } = useTranslation();
    const isOnboarding = tableMode === 'onboarding';
    const showSelection = Boolean(onBulkRoleAssign);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [reminderSendingId, setReminderSendingId] = useState<string | null>(
        null
    );
    const [deleteInviteTarget, setDeleteInviteTarget] =
        useState<UserRow | null>(null);
    const [deletingInviteId, setDeletingInviteId] = useState<string | null>(
        null
    );

    const selectableIds = users
        .filter((user) => user.selectable !== false)
        .map((user) => user._id);
    const allSelected =
        selectableIds.length > 0 &&
        selectableIds.every((id) => selectedIds.has(id));

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(selectableIds));
        }
    };

    const handleBulkRole = (roleId: string) => {
        if (onBulkRoleAssign) {
            onBulkRoleAssign(
                [...selectedIds].filter((id) => selectableIds.includes(id)),
                roleId
            );
        }
        setSelectedIds(new Set());
    };

    const handleSendReminder = async (user: UserRow) => {
        if (!onSendReminder) return;
        setReminderSendingId(user._id);
        try {
            await onSendReminder(user);
        } finally {
            setReminderSendingId(null);
        }
    };

    const handleDeleteInvite = async () => {
        if (!deleteInviteTarget || !onRemove) return;

        setDeletingInviteId(deleteInviteTarget._id);
        try {
            const result = await onRemove(deleteInviteTarget._id);
            if (result !== false) setDeleteInviteTarget(null);
        } catch {
            // Caller handles feedback.
        } finally {
            setDeletingInviteId(null);
        }
    };

    const totalColumnCount =
        (showSelection ? 1 : 0) +
        2 +
        (isOnboarding ? 0 : 1) +
        (!isOnboarding && onSendReminder ? 1 : 0) +
        1;
    const selectionActive = showSelection && selectedIds.size > 0;

    return (
        <div className="space-y-3">
            <Table containerClassName="rounded-lg border border-white/5 bg-white/5">
                <TableHeader className="bg-white/5 [&_tr]:border-white/5">
                    {selectionActive ? (
                        <TableRow className="hover:bg-transparent">
                            <TableHead colSpan={totalColumnCount}>
                                <div className="flex items-center gap-3">
                                    <span className="shrink-0 text-xs font-medium text-foreground">
                                        {t('facilities.invitationsTable.selected', { count: selectedIds.size })}
                                    </span>
                                    <Select onValueChange={handleBulkRole}>
                                        <SelectTrigger className="h-7 w-auto border-white/20 bg-white/10 text-xs text-foreground">
                                            <SelectValue placeholder={t('facilities.invitationsTable.assignRole')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {roles.map((role) => (
                                                <SelectItem key={role._id} value={role._id}>
                                                    {getActorDisplayName(role)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </TableHead>
                        </TableRow>
                    ) : (
                        <TableRow className="hover:bg-transparent">
                            {showSelection && (
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={allSelected}
                                        onCheckedChange={toggleSelectAll}
                                        aria-label={t('facilities.invitationsTable.selectAll')}
                                    />
                                </TableHead>
                            )}
                            <TableHead>{t('facilities.invitationsTable.email')}</TableHead>
                            <TableHead>{t('facilities.invitationsTable.role')}</TableHead>
                            {!isOnboarding && <TableHead>{t('facilities.invitationsTable.sent')}</TableHead>}
                            {!isOnboarding && onSendReminder && (
                                <TableHead>{t('facilities.invitationsTable.reminder')}</TableHead>
                            )}
                            <TableHead className="text-right">{t('facilities.invitationsTable.actions')}</TableHead>
                        </TableRow>
                    )}
                </TableHeader>
                <TableBody>
                    {users.length === 0 ? (
                        <TableRow className="hover:bg-transparent">
                            <TableCell
                                colSpan={
                                    (showSelection ? 1 : 0) +
                                    2 +
                                    (isOnboarding ? 0 : 1) +
                                    (!isOnboarding && onSendReminder ? 1 : 0) +
                                    1
                                }
                                className="py-10 text-center text-xs text-muted-foreground"
                            >
                                {emptyMessage}
                            </TableCell>
                        </TableRow>
                    ) : (
                        users.map((user) => (
                            <TableRow
                                key={user._id}
                                className={`border-white/5 ${
                                    selectedIds.has(user._id)
                                        ? 'bg-white/5'
                                        : ''
                                }`}
                            >
                                {showSelection && (
                                    <TableCell className="w-10">
                                        {user.selectable !== false && (
                                            <Checkbox
                                                checked={selectedIds.has(
                                                    user._id
                                                )}
                                                onCheckedChange={() =>
                                                    toggleSelect(user._id)
                                                }
                                                aria-label={t('facilities.invitationsTable.selectUser', { email: user.email })}
                                            />
                                        )}
                                    </TableCell>
                                )}
                                <TableCell>
                                    <span className="text-xs text-foreground">
                                        {user.email}
                                        {user.isCurrentUser && (
                                            <span className="ml-1.5 text-muted-foreground">
                                                {t('facilities.invitationsTable.you')}
                                            </span>
                                        )}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    {onRoleChange &&
                                    user.editable !== false ? (
                                        <Select
                                            value={user.roleId || undefined}
                                            onValueChange={(val) =>
                                                onRoleChange(user._id, val)
                                            }
                                        >
                                            <SelectTrigger className={`h-7 w-auto text-xs ${!user.roleId ? 'border-amber-500/30 text-amber-400' : ''}`}>
                                                <SelectValue
                                                    placeholder={
                                                        user.role !== 'Pending'
                                                            ? translateRoleName(user.role, user.roleId, roles)
                                                            : t('facilities.invitationsTable.selectRole')
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {getInvitationRoleOptions(
                                                    user,
                                                    roles
                                                ).map((role) => (
                                                    <SelectItem
                                                        key={role._id}
                                                        value={role._id}
                                                    >
                                                        {getActorDisplayName(role)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">
                                            {translateRoleName(user.role, user.roleId, roles)}
                                        </span>
                                    )}
                                </TableCell>
                                {!isOnboarding && (
                                    <TableCell>
                                        <span className="text-xs text-muted-foreground">
                                            {user.sentAt
                                                ? formatShortDate(user.sentAt)
                                                : '—'}
                                        </span>
                                    </TableCell>
                                )}
                                {!isOnboarding && onSendReminder && (
                                    <TableCell>
                                        <div className="flex flex-col items-start gap-1">
                                            {getLatestReminderDate(user) && (
                                                <span className="text-xs text-muted-foreground">
                                                    {t('facilities.invitationsTable.lastReminder', {
                                                        date: formatShortDate(
                                                            getLatestReminderDate(
                                                                user
                                                            ) as string
                                                        ),
                                                    })}
                                                </span>
                                            )}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-sky-300 hover:text-sky-200"
                                                disabled={
                                                    reminderSendingId ===
                                                    user._id
                                                }
                                                onClick={() =>
                                                    handleSendReminder(user)
                                                }
                                            >
                                                {reminderSendingId === user._id
                                                    ? t('facilities.invitationsTable.sending')
                                                    : t('facilities.invitationsTable.sendReminder')}
                                            </Button>
                                        </div>
                                    </TableCell>
                                )}
                                <TableCell className="text-right">
                                    {user.removable && onRemove ? (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-400"
                                            onClick={() => {
                                                if (user.status === 'draft') {
                                                    onRemove(user._id);
                                                } else {
                                                    setDeleteInviteTarget(user);
                                                }
                                            }}
                                            aria-label={
                                                user.status === 'draft'
                                                    ? t('facilities.invitationsTable.removeInvitation')
                                                    : t('facilities.invitationsTable.deleteInvite')
                                            }
                                        >
                                            <Trash2
                                                className="h-3.5 w-3.5"
                                                aria-hidden="true"
                                            />
                                        </Button>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
            <AlertDialog
                open={Boolean(deleteInviteTarget)}
                onOpenChange={(open) => {
                    if (!open) setDeleteInviteTarget(null);
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('facilities.invitationsTable.deleteTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteInviteTarget
                                ? t('facilities.invitationsTable.deleteDesc', { email: deleteInviteTarget.email })
                                : t('facilities.invitationsTable.deleteDescFallback')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            disabled={Boolean(deletingInviteId)}
                        >
                            {t('facilities.invitationsTable.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteInvite}
                            disabled={Boolean(deletingInviteId)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deletingInviteId ? t('facilities.invitationsTable.deleting') : t('facilities.invitationsTable.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function getLatestReminderDate(user: UserRow): string | null {
    const auto = user.reminderSentAt ?? null;
    const manual = user.lastManualReminderAt ?? null;
    if (!auto && !manual) return null;
    if (!auto) return manual;
    if (!manual) return auto;
    return new Date(auto) > new Date(manual) ? auto : manual;
}

function translateRoleName(
    role: string,
    roleId: string | undefined,
    roles: RoleOption[]
): string {
    const option = roleId
        ? roles.find((r) => r._id === roleId)
        : roles.find((r) => r.role === role);
    return option ? getActorDisplayName(option) : role;
}

function getInvitationRoleOptions(
    user: UserRow,
    roles: RoleOption[]
): RoleOption[] {
    const hasCurrentRole = roles.some(
        (role) =>
            role._id === user.roleId ||
            role.role.trim().toLowerCase() === user.role.trim().toLowerCase()
    );

    if (hasCurrentRole || !user.roleId) return roles;

    return [{ _id: user.roleId, role: user.role }, ...roles];
}
